import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ThemeContext } from 'styled-components';
import debounce from 'lodash/debounce';
// components
import Button from '../Button';
import CaratRightIcon from '../../foundations/Icon/icons/utility/CaratRight';
import { env } from '../../docs/utils/docs.config';
import ProgressBar from '../ProgressBar';
// styles
import {
  ButtonStyledHeaderPrevCta,
  ColStyledContent,
  ColStyledFooterControls,
  ColStyledHeaderControls,
  ContainerStyledModal,
  DismissStyled,
  DivStyledLogoContainer,
  ModalStyled,
  RowStyledFooterControls,
  RowStyledHeaderControls,
  RowStyledContent,
  SpanStyledHeaderNmLogo,
} from './styles';

export const setModalParentHeight = (modalWindow, variant, windowInnerHeight) => {
  if (modalWindow) {
    const thisMaxModalHeight = variant === 'large' ? 600 : 300;
    const thisModalParent = modalWindow.closest('.nmx-modal');
    const thisModalContentRow = modalWindow.querySelector('.nmx-row-modal-content');

    if (variant === 'immersiveFlow') {
      thisModalParent.style.height = '100%';
    } else if (windowInnerHeight < thisMaxModalHeight) {
      thisModalParent.style.height = 'auto';
    } else {
      thisModalParent.style.height = 'fit-content';
    }
    if (thisModalContentRow) {
      if (windowInnerHeight > thisMaxModalHeight) {
        thisModalContentRow.style.overflowY = 'auto';
      } else {
        thisModalContentRow.style.overflowY = 'hidden';
      }
    }
  }
};

export const setupResizeListener = (resizeListenerRef, modalWindow, setModalParentHeight, variant, LISTENER_THROTTLE) => {
  if (typeof window !== 'undefined' && modalWindow.current) {
    resizeListenerRef.current = debounce(() => {
      setModalParentHeight(modalWindow.current, variant, window.innerHeight);
    }, LISTENER_THROTTLE);

    window.addEventListener('resize', resizeListenerRef.current);
  }
};

export const cleanupResizeListener = (resizeListenerRef) => {
  if (resizeListenerRef.current) {
    window.removeEventListener('resize', resizeListenerRef.current);
    resizeListenerRef.current.cancel();
    resizeListenerRef.current = null;
  }
};

export const fixSafariModalPosition = (isSafari, mmCheckMedium, modalContent) => {
  if (isSafari && !mmCheckMedium()) {
    modalContent.style.position = 'static'; // Safari fix for mobile profile modal getting squished down to 1px horizontal line
  }
};

export const handleAfterOpenModal = ({
  modalWindow,
  resizeListenerRef,
  setModalParentHeight,
  variant,
  LISTENER_THROTTLE,
  onModalAfterOpen,
  isSafari,
  mmCheckMedium,
}) => {
  const modalContent = document.querySelector('.ReactModal__Content--after-open');
  fixSafariModalPosition(isSafari, mmCheckMedium, modalContent);

  setModalParentHeight(modalWindow.current, variant, window.innerHeight);

  setupResizeListener(resizeListenerRef, modalWindow, setModalParentHeight, variant, LISTENER_THROTTLE);

  onModalAfterOpen();
};

export const ModalComponent = ({
  backgroundVariant = 'lightA',
  branding = 'nm',
  buttonCloseAriaLabel = 'dismiss modal',
  buttonCloseClassName = 'nmx-button-close-modal',
  buttonCloseOnClick = () => {},
  buttonOpenAriaLabel,
  buttonOpenClassName,
  buttonOpenId = 'nmx-button-open-modal',
  buttonOpenOnClick = () => {},
  buttonOpenVariant = 'primary',
  buttonCloseId,
  buttonText,
  children,
  customModalContentStyles,
  dataTestIdButtonModalOpen = undefined,
  dataTestIdModal = undefined,
  modalAriaLabel = 'Northwestern Mutual Modal',
  modalClassName = '',
  modalFooter,
  modalHeader,
  modalId,
  modalIsOpen = false,
  modalName,
  onModalAfterOpen = () => {},
  modalParentSelector = '#nmx-header',
  themeType = 'lightTheme',
  variant = 'small',
}) => {
  const theme = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(modalIsOpen);
  const [thisWindowInnerWidth, setWindowInnerWidth] = useState(window.innerWidth);
  const [thisWindowInnerHeight, setWindowInnerHeight] = useState(window.innerHeight);

  // refs
  const modalWindow = useRef();
  const resizeListenerRef = useRef();

  const { basePath, nmxTemplateVersion } = env;

  const isSafari = /^((?!chrome|android).)*safari/i.test(
    typeof navigator !== 'undefined' ? navigator.userAgent : '',
  );
  const LISTENER_THROTTLE = 100;

  const mmCheckMedium = () => typeof document !== 'undefined'
    && window.matchMedia('(min-width: 768px)').matches;

  const openModal = () => {
    setIsOpen(true);
    buttonOpenOnClick();
  };

  const closeModal = () => {
    setIsOpen(false);
    buttonCloseOnClick();
  };

  const afterOpenModal = () => {
    handleAfterOpenModal({
      modalWindow,
      resizeListenerRef,
      setModalParentHeight,
      variant,
      LISTENER_THROTTLE,
      onModalAfterOpen,
      isSafari,
      mmCheckMedium,
    });
  };

  const afterCloseModal = () => {
    cleanupResizeListener(resizeListenerRef);
  };

  return (
    <>
      <Button
        ariaLabel={buttonOpenAriaLabel}
        className={buttonOpenClassName}
        data-testid={dataTestIdButtonModalOpen}
        id={buttonOpenId}
        onClick={openModal}
        variant={buttonOpenVariant}>
        {buttonText}
      </Button>
      {/** list of props and default options: https://reactcommunity.org/react-modal/ */}
      {/** For optimal accessibility, these prop options should remain as default: shouldCloseOnEsc=true, shouldCloseOnOverlayClick=true, shouldReturnFocusAfterClose=true, preventScroll=false */}
      <ModalStyled
        ariaHideApp={false}
        backgroundVariant={backgroundVariant}
        bodyOpenClassName='modal-is-open'
        branding={branding}
        className='nmx-modal'
        contentLabel={modalAriaLabel}
        isOpen={isOpen}
        onAfterClose={afterCloseModal}
        onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        overlayClassName='nmx-modal-overlay'
        parentSelector={() => document.querySelector(modalParentSelector)} // bind Modal to element
        // why are we injecting styles here when this is already a styled component?
        style={{
          // Overlay is persistent for all variants per accessibility standards
          overlay: {
            // idiomatic css ordering
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            overflow: 'auto',
            zIndex: theme.zIndex.modalOverlay,
          },
          content: {
            // idiomatic css ordering
            position: 'absolute',
            margin: 'auto',
            padding: 0,
            WebkitOverflowScrolling: 'touch',
            outline: 'none',
            ...customModalContentStyles,
          },
        }}
        themeType={themeType}
        variant={variant}
        windowInnerHeight={thisWindowInnerHeight}
        windowInnerWidth={thisWindowInnerWidth}
      >
        <ContainerStyledModal
          aria-modal='true'
          className={classNames(modalClassName, 'nmx-modal-content')}
          {...(modalId && { id: modalId })}
          ref={modalWindow}
          role='dialog'
          variant={variant}>
          {modalHeader && variant === 'immersiveFlow'
            && <RowStyledHeaderControls
              variant={variant}
              className='nmx-row-modal-header'>
              <ColStyledHeaderControls
                className='nmx-col-modal-header'
                {...(modalHeader.prevCta && { prevCtaControl: modalHeader.prevCta })}
              >
                {/** NM Logo available for small/large modals, too? */}
                {modalHeader.displayNmLogo
                && <DivStyledLogoContainer className='nmx-logo-container'>
                  <SpanStyledHeaderNmLogo
                    aria-label='Northwestern Mutual'
                    basePath={basePath}
                    nmxTemplateVersion={nmxTemplateVersion}
                  />
                </DivStyledLogoContainer>
                }
                {modalHeader.progressBar
                && <ProgressBar
                  fillPercentage={modalHeader.progressBar.fillPercentage}
                  themeType={modalHeader.progressBar.themeType}
                />
                }
                {modalHeader.prevCta
                && <ButtonStyledHeaderPrevCta
                  disabled={modalHeader.prevCta.buttonIsDisabled}
                  id={`prev-button-${modalName}`}
                  onClick={modalHeader.prevCta.buttonOnClick}
                  startAdornment={CaratRightIcon}
                  startAdornmentStylesReset
                  variant='tertiary'
                >{modalHeader.prevCta.buttonText}</ButtonStyledHeaderPrevCta>
                }
                {modalHeader.customHeaderChildren}
              </ColStyledHeaderControls>
            </RowStyledHeaderControls>
          }
          <RowStyledContent
            className='nmx-row-modal-content'
            modalFooter={modalFooter}
            // modalHeader={modalHeader}
            windowInnerHeight={thisWindowInnerHeight}
            variant={variant}
          >
            <ColStyledContent
              className='nmx-col-modal-content'
              modalFooter={modalFooter}
              modalHeader={modalHeader}
              {...modalHeader && { prevCtaControl: modalHeader.prevCta }}
              // prevCtaControl={modalHeader.prevCta}
              variant={variant}
            >
              {children}
              {/* if modalFooter controls not activated and using default/in-page header controls, cta must be passed into children prop at consumption repo */}
            </ColStyledContent>
          </RowStyledContent>
          {modalFooter
            && <RowStyledFooterControls
              className='nmx-row-modal-footer'
              modalFooter={modalFooter}
              modalHeader={modalHeader}
              {...modalHeader && { prevCtaControl: modalHeader.prevCta }}
              variant={variant}>
              <ColStyledFooterControls className='nmx-col-modal-footer'>
                {modalFooter.customFooterChildren}
                {/* show prevCta and nextCta if not customFooterChildren? */}
                {modalFooter.prevCta
                && <Button
                  disabled={modalFooter.prevCta.buttonIsDisabled}
                  id={`prev-button-${modalName}`}
                  onClick={modalFooter.prevCta.buttonOnClick}
                  variant='secondary'
                >{modalFooter.prevCta.buttonText}</Button>
                }
                {modalFooter.nextCta
                && <Button
                  disabled={modalFooter.nextCta.buttonIsDisabled}
                  id={`next-button-${modalName}`}
                  onClick={modalFooter.nextCta.buttonOnClick}
                >{modalFooter.nextCta.buttonText}</Button>
                }
              </ColStyledFooterControls>
            </RowStyledFooterControls>
          }
          {/** Dismiss is persistent for all variants per accessibility standards */}
          <DismissStyled
            ariaLabel={buttonCloseAriaLabel}
            {...buttonCloseId && { id: buttonCloseId }}
            className={classNames(buttonCloseClassName, 'nmx-button-close-modal')}
            onClick={closeModal}
          />
        </ContainerStyledModal>
      </ModalStyled>
    </>
  );
};

ModalComponent.propTypes = {
  /**  backgroundVariant determines the look and feel of the Section */
  backgroundVariant: PropTypes.oneOf([
    'darkDefault',
    'lightA',
    'lightB',
    'lightC',
  ]).isRequired,
  /** sets overall branding of component or module, default='nm' */
  branding: PropTypes.oneOf(['nm', 'pcg']),
  /** optional button close aria label */
  buttonCloseAriaLabel: PropTypes.string,
  /** optional button close className hook for custom styling */
  buttonCloseClassName: PropTypes.string,
  /** optional button close id hook for custom action */
  buttonCloseId: PropTypes.string,
  /** Optional onClick function bound to Dismiss/Close Button */
  buttonCloseOnClick: PropTypes.func,
  /** optional button open aria label */
  buttonOpenAriaLabel: PropTypes.string,
  /** optional button open className hook for custom styling */
  buttonOpenClassName: PropTypes.string,
  /** required button open id hook for custom action */
  buttonOpenId: PropTypes.string.isRequired,
  /** Optional onClick function bound to Launch Modal Button */
  buttonOpenOnClick: PropTypes.func,
  /** Optional prop to change variant of main open CTA */
  buttonOpenVariant: PropTypes.oneOf([ // add buttonOpenThemeType? add buttonOpenEndAdornment?
    'primary',
    'secondary',
    'tertiary',
  ]),
  /** required button inner text/children */
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  /** Children components placed inside the modal */
  children: PropTypes.any.isRequired,
  customModalContentStyles: PropTypes.object,
  /** data attribute used on the modal open button */
  dataTestIdButtonModalOpen: PropTypes.string,
  /** data attribute used on the modal */
  dataTestIdModal: PropTypes.string,
  /** required modal aria label */
  modalAriaLabel: PropTypes.string,
  /** Optional modal classname hook for custom styling */
  modalClassName: PropTypes.string,
  /** Optional modal footer with actionable CTAs */
  modalFooter: PropTypes.shape({
    /** bottom controls mimic CX forgot password screen, NMcom controlsVariant is for preclient experiences */
    /** Allows for custom content/controls in modalFooter  */
    customFooterChildren: PropTypes.any,
    /** allows custom modalFooter backgroundColor, default white */
    footerBackgroundColor: PropTypes.string,
    nextCta: PropTypes.shape({
      buttonIsDisabled: PropTypes.bool,
      buttonOnClick: PropTypes.func.isRequired,
      buttonText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]).isRequired,
    }),
    prevCta: PropTypes.shape({
      buttonIsDisabled: PropTypes.bool,
      buttonOnClick: PropTypes.func.isRequired,
      buttonText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]).isRequired,
    }),
  }),
  /** Optional modal footer actionable CTAs */
  modalHeader: PropTypes.shape({
    /** header controls for default NMcom preclient experiences */
    /** Allows for custom content/controls in modalHeader  */
    /** will nextCta ever be used? check with ECC */
    customHeaderChildren: PropTypes.any,
    /** display NM logo */
    displayNmLogo: PropTypes.bool,
    prevCta: PropTypes.shape({
      buttonIsDisabled: PropTypes.bool,
      buttonOnClick: PropTypes.func.isRequired,
      buttonText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]).isRequired,
    }),
    progressBar: PropTypes.shape({
      fillPercentage: PropTypes.number,
      themeType: PropTypes.oneOf(['lightTheme', 'darkTheme']),
    }),
  }),
  /** Optional modal id hook for custom action */
  modalId: PropTypes.string,
  /** Optional boolean to open modal without clicking CTA */
  modalIsOpen: PropTypes.bool,
  /** Optional string used in identifying and tagging CTAs and other modal assets */
  modalName: PropTypes.string,
  /** required modal parent selector - set to #nmx-header by default for in-page use */
  modalParentSelector: PropTypes.string.isRequired,
  /** Optional onModalAfterOpen function called after modal is open */
  onModalAfterOpen: PropTypes.func,
  /** Optional themeType */
  themeType: PropTypes.oneOf(['lightTheme', 'darkTheme']),
  /** Optional modal variant */
  variant: PropTypes.oneOf(['small', 'large', 'immersiveFlow']).isRequired,
};

export default ModalComponent;
